// NOTE: this is very much MVP! There has been a LOT of churn here while we test
// different insertion logic. This will be cleaned up heavily once we're satisfied
// with the approach. - AB
import {
  MOBILE_ONLY_AD_SIZE_MAPPING,
  TABLET_ONLY_AD_SIZE_MAPPING,
  TABLET_AND_DESKTOP_AD_SIZE_MAPPING,
  DESKTOP_ONLY_AD_SIZE_MAPPING,
  DEFAULT_AD_SIZE_MAPPING,
  RAIL_AD_SIZE_MAPPING,
  IN_POST_AD_SIZE_MAPPING_MOBILE,
  IN_POST_AD_SIZE_MAPPING_DESKTOP
} from 'constants/ads/sizes';
import {
  BASE_AD_UNITS,
  BASE_AD_UNITS_AMP,
  MAX_SUB_UNIT_COUNT,
  RAIL_REFRESH_INTERVAL
} from 'constants/ads/units';
import {
  IN_POST_AD_FIRST_OFFSET_MOBILE,
  IN_POST_AD_OFFSET_MOBILE,
  IN_POST_AD_FIRST_OFFSET_DESKTOP,
  IN_POST_AD_OFFSET_DESKTOP,
  STREAM_AD_OFFSET,
  HIDE_IN_POST_ADS,
  HIDE_POST_RAIL_ADS,
  HIDE_POST_FOOTER_ADS
} from 'constants/ads/injector';
import { JW_DEFAULT_PLAYLIST_URL } from 'constants/video';
import { pad, capitalize } from 'util/string';
import { counter, isSizeEnabled, hasVideo } from './helpers';

export const AD_TYPE = 'ad';
export const IN_POST_VIDEO_TYPE = 'inPostVideoAd';
export const RAIL_VIDEO_TYPE = 'railVideoAd';

const AMP_IN_POST_REFRESH_INTERVAL = 30;

const getAdItem = (id, data) => ({
  // id must be deterministic since we use it as a key, i.e. ad injection should
  // be pure (the same input should always return the same output). A global or
  // random id generator would not produce this output.
  id: `ad-${id}`,
  type: AD_TYPE,
  data
});

export const streamInjector = (bundle, getNextAdUnit, isAmp = false) => {
  const { data, layout, items } = bundle;
  const { targeting, enabled } = data.ads || {};
  // A counter for getting the next ad id
  const getAdId = counter(0);
  // Calling this function creates a new ad item with the correct namespace,
  // and properties
  const adFactory = (sizes, ops = {}) =>
    getAdItem(getAdId(), {
      unit: getNextAdUnit(),
      targeting,
      sizes,
      ...ops
    });

  let ads = [];

  if (layout === 'interrupter') {
    // Insert a single responsive ad if the bundle is an interrupter
    ads = ads.concat(adFactory(DEFAULT_AD_SIZE_MAPPING));
  } else {
    // Handle A-Z and stream bundles
    const showMobileAds = isSizeEnabled(enabled, 'mobile');
    const showTabletAds = isSizeEnabled(enabled, 'tablet');
    const showDesktopAds = isSizeEnabled(enabled, 'desktop');

    // Mobile ad insertion
    if (showMobileAds) {
      // Determine mobile ad count using the number of items in the bundle
      // Note, at mimimum there will always be at least 1 ad
      const mobileAdCount = Math.floor(items.length / STREAM_AD_OFFSET) || 1;
      // Create ad items
      ads = ads.concat(
        new Array(mobileAdCount).fill(null).map(() =>
          adFactory(MOBILE_ONLY_AD_SIZE_MAPPING, {
            sizeLabel: 'mobile'
          })
        )
      );
    }

    if (!isAmp) {
      // Tablet and desktop ad insertion
      if (showTabletAds && showDesktopAds) {
        // Add tablet and desktop ad
        ads = ads.concat(
          adFactory(TABLET_AND_DESKTOP_AD_SIZE_MAPPING, {
            sizeLabel: 'tablet-desktop'
          })
        );
      } else if (showTabletAds) {
        // Add tablet only ad
        ads = ads.concat(
          adFactory(TABLET_ONLY_AD_SIZE_MAPPING, {
            sizeLabel: 'tablet'
          })
        );
      } else if (showDesktopAds) {
        // Add desktop only ad
        ads = ads.concat(
          adFactory(DESKTOP_ONLY_AD_SIZE_MAPPING, {
            sizeLabel: 'desktop'
          })
        );
      }
    }
  }
  // Append ads to items
  return Object.assign({}, bundle, {
    items: [...items, ...ads]
  });
};

// Get in post ad offsets for mobile and desktop insertion
export const getInPostAdOffsets = (
  items,
  { device, firstAdOffset, nextAdOffset, injectVideoAd = false }
) => {
  // Find offsets where ads _could_ be inserted
  // Basic rules are:
  // - Ads should never be inserted after a heading
  // - Ads should never be inserted between an image + heading combo
  // - An ad should never be inserted after the last post item
  const offsets = items.reduce((p, item, i, a) => {
    const nextItem = a[i + 1];

    if (nextItem === undefined) {
      // Do not insert an ad after the last post item
      return p;
    }

    if (item.type === 'heading') {
      // Do not insert an ad after a heading
      return p;
    }

    if (nextItem.type === 'heading' && item.type === 'image') {
      // Do not insert an ad between an image + heading combo, i.e. effectively
      // consider it a heading
      return p;
    }

    return p.concat(i);
  }, []);

  return [
    // Insert first ad at firstAdOffset
    offsets[firstAdOffset - 1],
    // Insert subsequent ads every nextAdOffset after the first ad
    ...offsets
      .slice(firstAdOffset + nextAdOffset - 1)
      .reduce((p, offset, i) => {
        if (i % nextAdOffset === 0) {
          // Insert an ad if this index is a multiple of nextAdOffset
          return p.concat(offset);
        }
        return p;
      }, [])
  ].map((offset, i) => ({
    // Conditionally inject a video ad as the 2nd item
    type: injectVideoAd && i === 1 ? 'video' : 'ad',
    offset,
    device
  }));
};

export const postInjector = (bundle, baseAdPath, isAmp) => {
  // A counter to get the next ad index
  const getNextInPostIndex = counter(0);
  const { ads } = bundle.data;
  const { targeting } = ads || {};
  // Check if the post contains a video embed
  const hasInPostVideo = hasVideo(bundle);
  // Calling this function creates a new in post ad item with the correct path,
  // and properties
  const inPostAdFactory = (sizes, ops = {}) => {
    const index = getNextInPostIndex();
    const name = isAmp ? 'AmpPost' : 'Post';
    // Get the correct unit. This could be a numbered unit, e.g. Post_001, or the
    // base ad path if the max unit is reached.
    let unit = baseAdPath;
    if (index <= MAX_SUB_UNIT_COUNT) {
      unit = `${baseAdPath}/${name}_${pad(index)}`;
    }
    const data = {
      unit,
      targeting,
      // AMP units have a single size, not a size mapping
      ...(isAmp ? { size: sizes } : { sizes }),
      // Set refresh on in post ads (AMP only)
      ...(isAmp
        ? { refreshInterval: AMP_IN_POST_REFRESH_INTERVAL }
        : undefined),
      ...ops
    };
    return getAdItem(`in-post-${index}`, data);
  };

  const hideInPostAds = HIDE_IN_POST_ADS || bundle.hideInPostAds;

  // Get mobile in post offsets, i.e. those between the post's items
  // If `hideInPostAds` is truthy don't calculate any mobile offsets
  const mobileAdOffsets = hideInPostAds
    ? []
    : getInPostAdOffsets(bundle.items, {
        device: 'mobile',
        firstAdOffset: IN_POST_AD_FIRST_OFFSET_MOBILE,
        nextAdOffset: IN_POST_AD_OFFSET_MOBILE,
        injectVideoAd: !hasInPostVideo
      });

  // Get desktop in post offsets
  // If `hideInPostAds` or `isAmp` is truthy don't calculate any mobile offsets
  const desktopAdOffsets =
    hideInPostAds || isAmp
      ? []
      : getInPostAdOffsets(bundle.items, {
          device: 'desktop',
          firstAdOffset: IN_POST_AD_FIRST_OFFSET_DESKTOP,
          nextAdOffset: IN_POST_AD_OFFSET_DESKTOP,
          injectVideoAd: false
        });

  // Combine ad offsets for mobile and desktop insertion
  const adOffsets = [...mobileAdOffsets, ...desktopAdOffsets];

  // Used to insert ads into post items by converting the offsets to actual ad
  // items
  const inPostAdReducer = (p, item, i) => {
    // Get the ads to insert at this offset (if any)
    const adsToInsert = adOffsets
      .filter(a => a.offset === i)
      .map(({ device, type }) => {
        if (type === 'video') {
          // Insert video ad
          return {
            id: 'video-interrupter',
            type: IN_POST_VIDEO_TYPE,
            data: {
              playlist: JW_DEFAULT_PLAYLIST_URL,
              placement: 'related',
              showTitle: true
            }
          };
        }
        if (isAmp) {
          // Insert AMP banner ad
          return inPostAdFactory([300, 250]);
        }
        // Insert banner ad
        return inPostAdFactory(
          device === 'mobile'
            ? IN_POST_AD_SIZE_MAPPING_MOBILE
            : IN_POST_AD_SIZE_MAPPING_DESKTOP,
          {
            sizeLabel: device === 'mobile' ? 'post-small' : 'post-large'
          }
        );
      });
    // Add result to accumulator
    return p.concat([item, ...adsToInsert]);
  };

  // AMP injection
  if (isAmp) {
    return {
      ...bundle,
      items: [
        // Build a new array of items with the inserted ads
        ...bundle.items.reduce(inPostAdReducer, [])
      ]
    };
  }

  const asideAds = HIDE_POST_RAIL_ADS
    ? []
    : [
        ...(hasInPostVideo
          ? []
          : [
              {
                id: 'video-right-rail',
                type: RAIL_VIDEO_TYPE,
                data: {
                  playlist: JW_DEFAULT_PLAYLIST_URL,
                  placement: 'right_rail',
                  showTitle: true
                }
              }
            ]),
        // Right rail ad
        getAdItem('right-rail', {
          unit: `${baseAdPath}/Right_Rail`,
          targeting,
          sizes: RAIL_AD_SIZE_MAPPING,
          refreshInterval: RAIL_REFRESH_INTERVAL,
          sizeLabel: 'post-large'
        })
      ];

  const belowAds = HIDE_POST_FOOTER_ADS
    ? []
    : [
        // Footer
        getAdItem('footer', {
          unit: `${baseAdPath}/Footer`,
          targeting,
          sizes: DEFAULT_AD_SIZE_MAPPING
        })
      ];

  // Standard injection
  return {
    ...bundle,
    // Inject 1 ad above post (desktop only)
    above: [
      inPostAdFactory(DESKTOP_ONLY_AD_SIZE_MAPPING, {
        sizeLabel: 'post-large'
      }),
      ...(bundle.above ? bundle.above : [])
    ],
    items: [
      // Build a new array of items with the inserted ads
      ...bundle.items.reduce(inPostAdReducer, [])
    ],
    aside: [...asideAds, ...(bundle.aside ? bundle.aside : [])],
    below: [...belowAds, ...(bundle.below ? bundle.below : [])]
  };
};

// Inject ads into "stream" pages, like home, category and collection
const streamPageInjector = (bundles, namespace) => {
  // Stream pages use a shared counter. The units on the page are numbered sequentially
  // and this numbering spans bundles, e.g. it might look like:
  // Bundle 1
  // - Home__001
  // - Home__002
  // Bundle 2
  // - Home__003
  // - Home__004
  const path = BASE_AD_UNITS[namespace];

  if (!path) {
    return bundles;
  }

  // A shared counter for stream page ads
  const getNextIndex = counter(0);
  // Gets the next ad unit, e.g. Home/Home_001, then Home/Home_002, etc
  const getNextAdUnit = () => {
    const index = getNextIndex();
    const unit =
      index <= MAX_SUB_UNIT_COUNT
        ? `/${capitalize(namespace)}_${pad(index)}`
        : '';
    return `${path}${unit}`;
  };
  // Return augmented bundles
  return bundles.reduce((p, bundle, i) => {
    const { data, layout } = bundle;
    const { targeting, enabled } = data.ads || {};
    const next = bundles[i + 1] || {};

    if (enabled && enabled.length === 0) {
      // Ads are disabled at all sizes for this bundle
      return p.concat(bundle);
    }
    // Check if the bundle layout is a–z, i.e. a teaser bundle, or stream
    // Note, special handling of "combo" layouts that fill a row, e.g. "t" is
    // 2/3 and "u" is 1/3. These appear as a single module on the page. We need
    // to insert an ad interrupter after the block.
    if ((layout === 't' && next.layout !== 'u') || layout === 'u') {
      // <t>
      // <interrupter>
      // <a-s,v-z>
      // Or...
      // <t>
      // <u>
      // <interrupter>
      // Return bundle and interrupter, instead of adding ads to bundle
      return p.concat([
        bundle,
        streamInjector(
          {
            id: `${bundle.id}-ad-interrupter`,
            layout: 'interrupter',
            data: {
              ads: {
                // Use targeting of current bundle for interrupter
                targeting
                // TODO: Use enabled value of current bundle for toggling device
                // specific ads in interrupter
                // enabled
              }
            },
            items: []
          },
          getNextAdUnit
        )
      ]);
    } else if (/^([a-sv-zA-SV-Z]|stream|interrupter)$/.test(layout)) {
      // Match a-z bundles (excluding t and u), or "stream"
      // Note, this can be reverted to /^([a-zA-Z]|stream)$/ when we have a
      // better solution for "t" and "u" bundles.
      return p.concat(streamInjector(bundle, getNextAdUnit));
    }
    return p.concat(bundle);
  }, []);
};

// Inject ads into post pages
const postPageInjector = (bundles, namespace, isAmp = false) => {
  const path = isAmp ? BASE_AD_UNITS_AMP[namespace] : BASE_AD_UNITS[namespace];

  if (!path) {
    return bundles;
  }

  return bundles.map(bundle => {
    const { layout } = bundle;

    if (layout === 'post') {
      // Each post bundle has its own numbering, i.e. Post_001 is the post's
      // leaderboard unit.
      return bundle.hideAds ? bundle : postInjector(bundle, path, isAmp);
    }

    if (layout === 'r') {
      // Recirc units are not numbered, and will always be <path>/Recirc
      return streamInjector(bundle, () => `${path}/Recirc`, isAmp);
    }
    return bundle;
  });
};

export default (bundles, namespace, isAmp) => {
  switch (namespace) {
    case 'home':
    case 'category':
    case 'collection':
      return streamPageInjector(bundles, namespace);
    case 'post':
      return postPageInjector(bundles, namespace, isAmp);
    default:
      return bundles;
  }
};
